/**
 * CongestionBadge -- small reusable badge showing grid congestion factor
 * as a percentage accessibility indicator with color coding.
 *
 * Green (>90%), Yellow (70-90%), Red (<70%)
 */

interface CongestionBadgeProps {
  avgFactor: number;         // 0.0 to 1.0
  constrainedHours: number;
  totalHours: number;
}

function getColor(pct: number): { bg: string; text: string; ring: string } {
  if (pct > 90) return { bg: 'bg-emerald-500/15', text: 'text-emerald-400', ring: 'ring-emerald-500/30' };
  if (pct > 70) return { bg: 'bg-amber-500/15', text: 'text-amber-400', ring: 'ring-amber-500/30' };
  return { bg: 'bg-red-500/15', text: 'text-red-400', ring: 'ring-red-500/30' };
}

export function CongestionBadge({ avgFactor, constrainedHours, totalHours }: CongestionBadgeProps) {
  const pct = Math.round(avgFactor * 100);
  const { bg, text, ring } = getColor(pct);

  return (
    <span
      className={`group relative inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${bg} ${text} ${ring}`}
    >
      {/* Filled bar indicator */}
      <span className="relative w-8 h-1.5 rounded-full bg-surface-600 overflow-hidden">
        <span
          className={`absolute inset-y-0 left-0 rounded-full ${
            pct > 90 ? 'bg-emerald-400' : pct > 70 ? 'bg-amber-400' : 'bg-red-400'
          }`}
          style={{ width: `${pct}%` }}
        />
      </span>
      {pct}% grid accessibility

      {/* Tooltip — common point of confusion, so spell it out */}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-80 rounded-md bg-surface-800 border border-border px-3 py-2 text-[11px] leading-snug text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10">
        <span className="block text-text-primary font-medium mb-1">
          What &ldquo;{pct}% grid accessibility&rdquo; means
        </span>
        Average effective displacement credit was ~{pct}% of the unconstrained
        value. The project&apos;s zone was transmission-constrained{' '}
        {constrainedHours.toLocaleString()} of{' '}
        {totalHours.toLocaleString()} hours.
        <span className="block mt-1.5 text-text-muted">
          <strong className="text-text-secondary">Not</strong> curtailment —
          your full metered energy flows into the model. In constrained hours
          the grid backs off cleaner local generation (e.g. nearby renewables)
          instead of the system-wide marginal fossil, so the consequential
          impact credit per MWh is lower.
        </span>
      </span>
    </span>
  );
}
